import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import "./InfoIcon.scss";

/** Enum-like placement values */
const INFO_ICON_PLACEMENT = Object.freeze({
  RIGHT: "right",
  LEFT: "left",
  TOP: "top",
  BOTTOM: "bottom",
});

const DEFAULT_GAP_PX = 8;
const VIEWPORT_PADDING_PX = 8;
const TOOLTIP_Z_INDEX = 10000;
const POSITION_FIXED = "fixed";

const InfoIcon = ({
  text = "",
  html = "",
  placement = INFO_ICON_PLACEMENT.RIGHT,
  flip = true,
  offset = DEFAULT_GAP_PX,
}) => {
  const anchorRef = useRef(null);
  const tooltipRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [viewportPosition, setViewportPosition] = useState({ top: 0, left: 0 });
  const [resolvedPlacement, setResolvedPlacement] = useState(placement);

  const hasContent =
    (typeof html === "string" && html.trim().length > 0) ||
    (typeof text === "string" && text.trim().length > 0);

  // ---------- helpers ----------
  function getPlacementOrder(preferredPlacement) {
    switch (preferredPlacement) {
      case INFO_ICON_PLACEMENT.LEFT:
        return [
          INFO_ICON_PLACEMENT.LEFT,
          INFO_ICON_PLACEMENT.RIGHT,
          INFO_ICON_PLACEMENT.TOP,
          INFO_ICON_PLACEMENT.BOTTOM,
        ];
      case INFO_ICON_PLACEMENT.TOP:
        return [
          INFO_ICON_PLACEMENT.TOP,
          INFO_ICON_PLACEMENT.BOTTOM,
          INFO_ICON_PLACEMENT.RIGHT,
          INFO_ICON_PLACEMENT.LEFT,
        ];
      case INFO_ICON_PLACEMENT.BOTTOM:
        return [
          INFO_ICON_PLACEMENT.BOTTOM,
          INFO_ICON_PLACEMENT.TOP,
          INFO_ICON_PLACEMENT.RIGHT,
          INFO_ICON_PLACEMENT.LEFT,
        ];
      case INFO_ICON_PLACEMENT.RIGHT:
      default:
        return [
          INFO_ICON_PLACEMENT.RIGHT,
          INFO_ICON_PLACEMENT.LEFT,
          INFO_ICON_PLACEMENT.TOP,
          INFO_ICON_PLACEMENT.BOTTOM,
        ];
    }
  }

  /** Compute top/left for a given side relative to the anchor & tooltip rectangles. */
  function computePosition(side, anchorRect, tooltipRect, gapPx) {
    if (side === INFO_ICON_PLACEMENT.RIGHT) {
      return {
        left: anchorRect.right + gapPx,
        top: anchorRect.top + anchorRect.height / 2 - tooltipRect.height / 2,
      };
    }
    else if (side === INFO_ICON_PLACEMENT.LEFT) {
      return {
        left: anchorRect.left - gapPx - tooltipRect.width,
        top: anchorRect.top + anchorRect.height / 2 - tooltipRect.height / 2,
      };
    }
    else if (side === INFO_ICON_PLACEMENT.TOP) {
      return {
        left: anchorRect.left + anchorRect.width / 2 - tooltipRect.width / 2,
        top: anchorRect.top - gapPx - tooltipRect.height,
      };
    }
    else {
      // BOTTOM
      return {
        left: anchorRect.left + anchorRect.width / 2 - tooltipRect.width / 2,
        top: anchorRect.bottom + gapPx,
      };
    }
  }

  /** Check if tooltip fully fits in viewport within padding. */
  function isFullyVisible(position, tooltipRect, viewportWidth, viewportHeight, viewportPaddingPx) {
    if (position.left < viewportPaddingPx) {
      return false;
    }
    if (position.top < viewportPaddingPx) {
      return false;
    }
    if (position.left + tooltipRect.width > viewportWidth - viewportPaddingPx) {
      return false;
    }
    if (position.top + tooltipRect.height > viewportHeight - viewportPaddingPx) {
      return false;
    }
    return true;
  }

  /** Clamp tooltip position within viewport as a fallback. */
  function clampToViewport(position, tooltipRect, viewportWidth, viewportHeight, viewportPaddingPx) {
    const clampedLeft = Math.max(
      viewportPaddingPx,
      Math.min(viewportWidth - tooltipRect.width - viewportPaddingPx, position.left)
    );
    const clampedTop = Math.max(
      viewportPaddingPx,
      Math.min(viewportHeight - tooltipRect.height - viewportPaddingPx, position.top)
    );
    return { left: clampedLeft, top: clampedTop };
  }

  /** Measure & compute final tooltip position and placement. Returns true if positioned. */
  const positionTooltip = () => {
    const anchorEl = anchorRef.current;
    const tooltipEl = tooltipRef.current;
    if (!anchorEl || !tooltipEl) {
      return false;
    }

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const viewportPaddingPx = VIEWPORT_PADDING_PX;

    const anchorRect = anchorEl.getBoundingClientRect();
    const tooltipRect = tooltipEl.getBoundingClientRect();

    const candidateOrder = getPlacementOrder(placement);

    let chosenPlacement = placement;
    let position = computePosition(placement, anchorRect, tooltipRect, offset);

    if (flip) {
      let foundFit = false;
      for (const candidateSide of candidateOrder) {
        const candidatePos = computePosition(candidateSide, anchorRect, tooltipRect, offset);
        if (isFullyVisible(candidatePos, tooltipRect, viewportWidth, viewportHeight, viewportPaddingPx)) {
          position = candidatePos;
          chosenPlacement = candidateSide;
          foundFit = true;
          break;
        }
      }
      if (!foundFit) {
        position = clampToViewport(position, tooltipRect, viewportWidth, viewportHeight, viewportPaddingPx);
        chosenPlacement = placement;
      }
    }
    else {
      position = clampToViewport(position, tooltipRect, viewportWidth, viewportHeight, viewportPaddingPx);
      chosenPlacement = placement;
    }

    setViewportPosition({ top: position.top, left: position.left });
    setResolvedPlacement(chosenPlacement);
    return true;
  };

  // Open/close with no initial flash
  const open = () => {
    setIsReady(false);
    setIsOpen(true);
  };
  const close = () => {
    setIsOpen(false);
  };

  // Position synchronously after paint when opening or when inputs change.
  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }
    const positioned = positionTooltip();
    if (positioned) {
      setIsReady(true);
    }
    // Also close on Escape
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, text, html, placement, flip, offset]);

  // Reposition on scroll/resize while open.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onScroll = () => positionTooltip();
    const onResize = () => positionTooltip();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Safe to return now (hooks are set up)
  if (!hasContent) {
    return null;
  }

  const tooltipNode = (
    <div
      ref={tooltipRef}
      role="tooltip"
      className={`info-icon-text info-icon-text--${resolvedPlacement}`}
      style={{
        position: POSITION_FIXED,
        top: viewportPosition.top,
        left: viewportPosition.left,
        zIndex: TOOLTIP_Z_INDEX,
        visibility: isReady ? "visible" : "hidden",
        opacity: isReady ? 1 : 0,
        transition: "opacity 120ms ease",
      }}
      onMouseEnter={open}
      onMouseLeave={close}
    >
      {html ? <div dangerouslySetInnerHTML={{ __html: html }} /> : text}
    </div>
  );

  return (
    <span
      className="info-icon"
      ref={anchorRef}
      onMouseEnter={open}
      onMouseLeave={close}
      onFocus={open}
      onBlur={close}
      tabIndex={0}
			aria-haspopup="dialog"
			data-test-class="info-icon-text"
    >
      <FontAwesomeIcon icon={faCircleInfo} data-test-class="info-icon-icon" />
      {isOpen && typeof document !== "undefined" && createPortal(tooltipNode, document.body)}
    </span>
  );
};

export { INFO_ICON_PLACEMENT, InfoIcon as default };